import type { QualFinalRecap } from '@multirx/shared/types/championship.js';
import { DIVISIONS } from '@multirx/shared/types/division.js';
import { labelOwnDriver } from '../auth/ownDriverLabel.js';
import { createPillButton } from '../ui/pillButton.js';

let activeOverlay: HTMLElement | null = null;

function divisionLabel(division: QualFinalRecap['division']): string {
  return DIVISIONS.find((d) => d.id === division)?.shortLabel ?? `D${division}`;
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

function buildFinalTableRows(recap: QualFinalRecap): string {
  return recap.rows
    .map((row, index) => {
      return `<tr class="championship-qual-recap-row">
        <td class="championship-qual-recap-cell championship-qual-recap-cell--rank">${index + 1}</td>
        <td class="championship-qual-recap-cell championship-qual-recap-cell--driver">${escapeHtml(labelOwnDriver(row.driverName, row.driverId))}</td>
        <td class="championship-qual-recap-cell championship-qual-recap-cell--pts">${row.qualPoints}</td>
        <td class="championship-qual-recap-cell championship-qual-recap-cell--dice">${row.totalDiceRolls}</td>
      </tr>`;
    })
    .join('');
}

export function showChampionshipQualFinalRecap(recap: QualFinalRecap, onClose?: () => void): void {
  activeOverlay?.remove();
  activeOverlay = null;

  const overlay = document.createElement('div');
  overlay.className =
    'championship-session-results-overlay championship-qual-recap-overlay championship-qual-final-recap-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Classement final des qualifications');

  const card = document.createElement('div');
  card.className =
    'play-race-results-card championship-session-results-card championship-qual-recap-card championship-qual-final-recap-card';

  const title = document.createElement('h2');
  title.className = 'play-race-results-title';
  title.textContent = 'Fin des qualifications';

  const subtitle = document.createElement('p');
  subtitle.className = 'play-race-results-subtitle championship-qual-recap-subtitle';
  subtitle.textContent = recap.hadTiebreaks
    ? `${divisionLabel(recap.division)} · Classement final après barrages`
    : `${divisionLabel(recap.division)} · Classement final des 4 manches`;

  const tableWrap = document.createElement('div');
  tableWrap.className = 'play-race-results-table-wrap';
  const table = document.createElement('table');
  table.className =
    'play-race-results-table championship-qual-recap-table championship-qual-final-recap-table';
  table.innerHTML = `<thead>
    <tr>
      <th scope="col">#</th>
      <th scope="col">Pilote</th>
      <th scope="col" title="Points qualification">Pts qualif</th>
      <th scope="col" title="Lancers de dé">🎲</th>
    </tr>
  </thead>`;
  const tbody = document.createElement('tbody');
  tbody.innerHTML = buildFinalTableRows(recap);
  table.appendChild(tbody);
  tableWrap.appendChild(table);

  const hint = document.createElement('p');
  hint.className = 'championship-qual-recap-hint';
  hint.textContent =
    'Classement qualifications pour les grilles. Le championnat se joue en demi-finales et finale. Les points de course (monnaie) sont crédités à part.';

  const actions = document.createElement('div');
  actions.className = 'play-race-results-actions';
  const closeBtn = createPillButton('Continuer', { variant: 'cta', large: true });
  const dismiss = (): void => {
    overlay.remove();
    if (activeOverlay === overlay) activeOverlay = null;
    onClose?.();
    window.location.hash = 'saisons';
  };
  if (closeBtn instanceof HTMLButtonElement) {
    closeBtn.type = 'button';
    closeBtn.onclick = dismiss;
  }
  actions.appendChild(closeBtn);

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) dismiss();
  });

  card.append(title, subtitle, tableWrap, hint, actions);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
  activeOverlay = overlay;
}

export function dismissChampionshipQualFinalRecap(): void {
  activeOverlay?.remove();
  activeOverlay = null;
}
